import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Building, Award, Filter, X, ChevronUp, ChevronDown, Calendar, ChevronLeft, ChevronRight, MapPin, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { getApparatusForEvent } from '../../utils/disciplineUtils';
import { levelService } from '../../services/supabaseService';
import './ResultsView.css';

const ResultsView = ({
  events,
  selectedEvent,
  setSelectedEvent,
  currentEvent,
  currentClubs,
  publicFilters,
  setPublicFilters,
  showFilters,
  setShowFilters,
  getUniqueClubs,
  resetFilters,
  getGymnastsByCategory,
  LEVEL_OPTIONS,
  FRANJA_OPTIONS,
  APPARATUS_OPTIONS
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [monthIndex, setMonthIndex] = useState(-1); // -1 = Todos
  const [configuredLevels, setConfiguredLevels] = useState(null); // niveles configurados del evento
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' }); // sorting state
  const [clubsSummaryOpen, setClubsSummaryOpen] = useState(false); // acordeón clubs
  const [clubsSummaryFilter, setClubsSummaryFilter] = useState(''); // filtro de club en resumen
  const [expandedClubs, setExpandedClubs] = useState({}); // clubes expandidos
  const [categoryPages, setCategoryPages] = useState({}); // paginación por categoría
  const ATHLETES_PER_PAGE = 15;

  // Abreviaturas conocidas para aparatos
  const APPARATUS_ABBR = {
    'Salto': 'SAL',
    'Barras asimétricas': 'ASI',
    'Viga de equilibrio': 'VIG',
    'Suelo': 'SUE',
    'Caballo con arzones': 'CAB',
    'Anillas': 'ANI',
    'Barras paralelas': 'PAR',
    'Barra fija': 'BAR',
    'Aro': 'ARO',
    'Pelota': 'PEL',
    'Mazas': 'MAZ',
    'Cinta': 'CIN',
    '5 Aros': '5AR',
    '3 Cintas + 2 Pelotas': 'MIX',
    'Ejercicio de Balance': 'BAL',
    'Ejercicio Dinámico': 'DIN',
    'Ejercicio Combinado': 'COM',
    'Equipo femenino': 'EQF',
    'Equipo masculino': 'EQM',
    'All Around individual': 'AA',
    'All Around conjunto': 'AA',
  };

  const getApparatusAbbr = (name) => {
    if (APPARATUS_ABBR[name]) return APPARATUS_ABBR[name];
    // Fallback: primeras 3 letras en mayúscula
    return name.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ]/g, '').substring(0, 3).toUpperCase();
  };

  // Filtrar solo eventos activos para usuarios públicos
  const activeEvents = events.filter(e => e.is_active !== false);

  // Nombres de meses en español
  const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Helper: obtener key de mes desde una fecha string
  const getMonthKey = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Extraer meses únicos de los eventos activos
  const availableMonths = useMemo(() => {
    const monthsSet = new Map();
    activeEvents.forEach(event => {
      if (event.date) {
        const d = new Date(event.date + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          const key = getMonthKey(event.date);
          if (key && !monthsSet.has(key)) {
            monthsSet.set(key, {
              key,
              year: d.getFullYear(),
              month: d.getMonth(),
              label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
            });
          }
        }
      }
    });
    return Array.from(monthsSet.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [activeEvents]);

  // Auto-seleccionar mes del evento actual al seleccionar un evento
  useEffect(() => {
    if (currentEvent && currentEvent.date && availableMonths.length > 0) {
      const eventMonthKey = getMonthKey(currentEvent.date);
      if (eventMonthKey) {
        const idx = availableMonths.findIndex(m => m.key === eventMonthKey);
        if (idx !== -1 && monthIndex !== idx) {
          setMonthIndex(idx);
        }
      }
    }
  }, [selectedEvent]); // Solo cuando cambia el evento seleccionado

  // Cargar niveles configurados del evento desde la BD
  useEffect(() => {
    if (!selectedEvent || !currentEvent) {
      setConfiguredLevels(null);
      return;
    }
    const loadEventLevels = async () => {
      try {
        const { data } = await levelService.getEventLevels(currentEvent.id);
        if (data && data.length > 0) {
          setConfiguredLevels(data.map(l => l.name).filter(Boolean));
        } else {
          setConfiguredLevels(null); // Sin config → fallback a gimnastas
        }
      } catch {
        setConfiguredLevels(null);
      }
    };
    loadEventLevels();
  }, [selectedEvent, currentEvent]);

  // Navegación del slider de meses
  const canGoPrev = monthIndex > -1;
  const canGoNext = monthIndex < availableMonths.length - 1;
  const currentMonthLabel = monthIndex === -1 ? 'Todos los meses' : availableMonths[monthIndex]?.label || 'Todos los meses';

  const goToPrevMonth = () => {
    if (canGoPrev) setMonthIndex(monthIndex - 1);
  };
  const goToNextMonth = () => {
    if (canGoNext) setMonthIndex(monthIndex + 1);
  };

  // Filtrar eventos por mes seleccionado
  const filteredEvents = useMemo(() => {
    if (monthIndex === -1 || !availableMonths[monthIndex]) return activeEvents;
    const selectedKey = availableMonths[monthIndex].key;
    return activeEvents.filter(event => {
      if (!event.date) return false;
      const key = getMonthKey(event.date);
      return key === selectedKey;
    });
  }, [activeEvents, monthIndex, availableMonths]);

  // Obtener aparatos disponibles basado en el evento
  const getAvailableApparatus = () => {
    if (currentEvent && currentEvent.discipline && currentEvent.category) {
      return getApparatusForEvent(currentEvent);
    }
    // Fallback a los aparatos antiguos
    return APPARATUS_OPTIONS.map(name => ({
      code: name.toLowerCase(),
      name: name,
      apparatus: name.toLowerCase()
    }));
  };

  const availableApparatus = getAvailableApparatus();

  // Niveles del evento: priorizar configuración del evento, fallback a gimnastas
  const eventLevels = useMemo(() => {
    // Si hay niveles configurados en el evento, usar esos
    if (configuredLevels && configuredLevels.length > 0) {
      return configuredLevels;
    }
    // Fallback: extraer de las gimnastas
    if (!currentClubs || !Array.isArray(currentClubs)) return LEVEL_OPTIONS;
    const levelsSet = new Set();
    currentClubs.forEach(club => {
      (club.gymnasts || []).forEach(g => {
        if (g.level) levelsSet.add(g.level);
      });
    });
    if (levelsSet.size === 0) return LEVEL_OPTIONS;
    const ordered = LEVEL_OPTIONS.filter(l => levelsSet.has(l));
    const extra = Array.from(levelsSet).filter(l => !LEVEL_OPTIONS.includes(l)).sort();
    return [...ordered, ...extra];
  }, [configuredLevels, currentClubs, LEVEL_OPTIONS]);

  const eventFranjas = useMemo(() => {
    if (!currentClubs || !Array.isArray(currentClubs)) return FRANJA_OPTIONS;
    const franjasSet = new Set();
    currentClubs.forEach(club => {
      (club.gymnasts || []).forEach(g => {
        if (g.franja) franjasSet.add(g.franja);
      });
    });
    if (franjasSet.size === 0) return FRANJA_OPTIONS;
    const ordered = FRANJA_OPTIONS.filter(f => franjasSet.has(f));
    const extra = Array.from(franjasSet).filter(f => !FRANJA_OPTIONS.includes(f)).sort();
    return [...ordered, ...extra];
  }, [currentClubs, FRANJA_OPTIONS]);

  // Auto-expand categories cuando se selecciona un filtro
  useEffect(() => {
    if (publicFilters.level || publicFilters.franja) {
      const newExpandedCategories = {};
      eventLevels.forEach(level => {
        eventFranjas.forEach(franja => {
          const categoryKey = `${level}-${franja}`;
          const categoryGymnasts = getGymnastsByCategory(level, franja);
          
          // Expandir si hay atletas en esta categoría
          if (categoryGymnasts.length > 0) {
            newExpandedCategories[categoryKey] = true;
          }
        });
      });
      setExpandedCategories(newExpandedCategories);
    } else {
      // Colapsar todo si no hay filtros
      setExpandedCategories({});
    }
    // Reset paginación al cambiar filtros
    setCategoryPages({});
  }, [publicFilters, getGymnastsByCategory, eventLevels, eventFranjas]);

  const toggleExpandCategory = (category) => {
    setExpandedCategories({
      ...expandedCategories,
      [category]: !expandedCategories[category]
    });
  };

  // Pagination helpers
  const getCategoryPage = (categoryKey) => categoryPages[categoryKey] || 0;
  const setCategoryPage = (categoryKey, page) => {
    setCategoryPages(prev => ({ ...prev, [categoryKey]: page }));
  };
  const paginateGymnasts = (gymnasts, categoryKey) => {
    const page = getCategoryPage(categoryKey);
    const start = page * ATHLETES_PER_PAGE;
    return gymnasts.slice(start, start + ATHLETES_PER_PAGE);
  };
  const getTotalPages = (total) => Math.ceil(total / ATHLETES_PER_PAGE);

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Toggle direction, or reset
        if (prev.direction === 'desc') return { key, direction: 'asc' };
        if (prev.direction === 'asc') return { key: 'total', direction: 'desc' }; // reset
      }
      return { key, direction: 'desc' };
    });
  };

  const sortGymnasts = (gymnasts) => {
    const sorted = [...gymnasts];
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (sortConfig.key === 'club') {
        aVal = a.clubName || '';
        bVal = b.clubName || '';
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (sortConfig.key === 'total') {
        aVal = a.total || 0;
        bVal = b.total || 0;
      } else {
        // apparatus key
        aVal = a.scores[sortConfig.key] || 0;
        bVal = b.scores[sortConfig.key] || 0;
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={10} className="sort-icon-neutral" />;
    if (sortConfig.direction === 'desc') return <ArrowDown size={10} className="sort-icon-active" />;
    return <ArrowUp size={10} className="sort-icon-active" />;
  };

  // Si no hay eventos activos
  if (activeEvents.length === 0) {
    return (
      <div className="no-event">
        <div className="no-event-icon">🏆</div>
        <h2>No hay competencias disponibles</h2>
        <p>Los administradores deben activar una competencia para que sea visible aquí</p>
      </div>
    );
  }

  // Si no hay un evento seleccionado o el evento actual no está activo, mostrar selector
  if (!selectedEvent || !currentEvent || !currentEvent.is_active) {
    return (
      <div className="results-view">
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ marginBottom: '32px' }}>
           
            <h1 className="results-hero-title">
              Selecciona una Competencia
            </h1>
            <p className="results-hero-subtitle">
              Elige la competencia de la que deseas ver los resultados
            </p>
          </div>

          {/* Filtro de mes - Slider */}
          {availableMonths.length > 0 && (
            <div className="month-slider-container">
              <button
                className={`month-slider-arrow ${!canGoPrev ? 'disabled' : ''}`}
                onClick={goToPrevMonth}
                disabled={!canGoPrev}
                aria-label="Mes anterior"
              >
                <ChevronLeft size={22} />
              </button>
              <div className="month-slider-display">
                <Calendar size={18} />
                <span className="month-slider-text">{currentMonthLabel}</span>
              </div>
              <button
                className={`month-slider-arrow ${!canGoNext ? 'disabled' : ''}`}
                onClick={goToNextMonth}
                disabled={!canGoNext}
                aria-label="Mes siguiente"
              >
                <ChevronRight size={22} />
              </button>
            </div>
          )}

          {filteredEvents.length === 0 ? (
            <div className="no-events-month">
              <p>No hay competencias para este mes</p>
              <button className="month-slider-reset" onClick={() => setMonthIndex(-1)}>
                Ver todas las competencias
              </button>
            </div>
          ) : (
          <div className="event-cards-grid">
            {filteredEvents.map(event => (
              <button
                key={event.id}
                onClick={() => setSelectedEvent(event.id)}
                className="event-card"
              >
                <div className="event-card-image">
                  {event.image_url ? (
                    <img
                      src={event.image_url}
                      alt={event.name}
                    />
                  ) : (
                    <div className="event-card-placeholder">
                      <Trophy size={48} color="white" />
                    </div>
                  )}
                </div>
                <div className="event-card-body">
                  <h3 className="event-card-title">{event.name}</h3>
                  <p className="event-card-date">📅 {event.date}</p>
                  {event.location && (
                    <p className="event-card-location">📍 {event.location}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="results-view">
      {/* Botón volver a selección de competencias */}
      {activeEvents.length > 1 && (
        <button
          className="back-to-events-btn"
          onClick={() => setSelectedEvent(null)}
        >
          <ChevronLeft size={18} />
          <span>Volver a competencias</span>
        </button>
      )}

      {/* Event Header */}
      <div className="event-header">
        <div className="event-header-content">
          <div className="event-header-main">
            {currentEvent.image_url ? (
              <img
                src={currentEvent.image_url}
                alt={currentEvent.name}
                className="event-image"
              />
            ) : (
              <div className="event-icon-large">
                <Trophy size={28} color="#71717a" />
              </div>
            )}
            <div className="event-header-text">
              <h1>{currentEvent.name}</h1>
              <p>
                <Calendar size={13} />
                {currentEvent.date}
                {currentEvent.location && (
                  <>
                    <span className="event-header-dot">·</span>
                    <MapPin size={13} />
                    {currentEvent.location}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-header">
          <div className="filters-title">
            <Filter size={20} color="#18181b" />
            <h2>Filtros de Resultados</h2>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
          >
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
        </div>

        {showFilters && (
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="filter-nivel">Nivel</label>
              <select
                id="filter-nivel"
                value={publicFilters.level}
                onChange={(e) => setPublicFilters({...publicFilters, level: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos los niveles</option>
                {eventLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-franja">Franja</label>
              <select
                id="filter-franja"
                value={publicFilters.franja}
                onChange={(e) => setPublicFilters({...publicFilters, franja: e.target.value})}
                className="filter-select"
              >
                <option value="">Todas las franjas</option>
                {eventFranjas.map(franja => (
                  <option key={franja} value={franja}>{franja}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-club">Club</label>
              <select
                id="filter-club"
                value={publicFilters.club}
                onChange={(e) => setPublicFilters({...publicFilters, club: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos los clubes</option>
                {getUniqueClubs().map(clubName => (
                  <option key={clubName} value={clubName}>{clubName}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-aparato">Aparato</label>
              <select
                id="filter-aparato"
                value={publicFilters.apparatus}
                onChange={(e) => setPublicFilters({...publicFilters, apparatus: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos (All-Round)</option>
                {availableApparatus.map(apparatus => (
                  <option key={apparatus.code} value={apparatus.name}>{apparatus.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="filters-footer">
          <div className="active-filters">
            {publicFilters.level || publicFilters.franja || publicFilters.club || publicFilters.apparatus ? 
              `Filtros aplicados: ${[
                publicFilters.level ? `Nivel: ${publicFilters.level}` : '',
                publicFilters.franja ? `Franja: ${publicFilters.franja}` : '',
                publicFilters.club ? `Club: ${publicFilters.club}` : '',
                publicFilters.apparatus ? `Ordenar por: ${publicFilters.apparatus}` : ''
              ].filter(Boolean).join(', ')}` : 
              'No hay filtros aplicados'}
          </div>
          {(publicFilters.level || publicFilters.franja || publicFilters.club || publicFilters.apparatus) && (
            <button
              onClick={resetFilters}
              className="clear-filters-btn"
            >
              <X size={16} />
              Limpiar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Message when no filters selected */}
      {!publicFilters.level && !publicFilters.franja && (
        <div className="no-filters-message">
          <Award size={48} color="#d1d5db" />
          <h3>Selecciona Nivel y Franja para ver resultados</h3>
          <p>Usa los filtros arriba para mostrar las atletas que deseas ver</p>
        </div>
      )}

      {/* Results by Category - Only show if filters are selected */}
      {(publicFilters.level || publicFilters.franja) && eventLevels.flatMap(level =>
        eventFranjas.map(franja => {
          const categoryGymnasts = getGymnastsByCategory(level, franja);
          if (categoryGymnasts.length === 0) return null;

          if (publicFilters.level && publicFilters.level !== level) return null;
          if (publicFilters.franja && publicFilters.franja !== franja) return null;

          const categoryKey = `${level}-${franja}`;
          const isExpanded = expandedCategories[categoryKey];

          return (
            <div key={categoryKey} className="category-card">
              <div className="category-header" onClick={() => toggleExpandCategory(categoryKey)}>
                <div className="category-title">
                  <Award size={16} color="#18181b" />
                  <h3>{level} - {franja}</h3>
                  {publicFilters.apparatus && publicFilters.apparatus !== 'Todos' && (
                    <span className="sorting-info">
                      (Ordenado por {publicFilters.apparatus})
                    </span>
                  )}
                </div>
                <div className="category-info">
                  <span className="gymnast-count">{categoryGymnasts.length} atletas</span>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {isExpanded && (
                <div className="gymnasts-table-wrapper">
                  <table className="gymnasts-table">
                    <thead>
                      <tr>
                        <th className="gt-col-rank">#</th>
                        <th className="gt-col-name sortable" onClick={() => handleSort('name')}>
                          Nombre {getSortIcon('name')}
                        </th>
                        <th className="gt-col-club sortable" onClick={() => handleSort('club')}>
                          Club {getSortIcon('club')}
                        </th>
                        {availableApparatus.map(app => (
                          <th
                            key={app.code}
                            className={`gt-col-app sortable ${publicFilters.apparatus === app.name ? 'highlighted' : ''}`}
                            onClick={() => handleSort(app.apparatus)}
                          >
                            <span className="app-full">{app.name}</span>
                            <span className="app-abbr">{getApparatusAbbr(app.name)}</span>
                            {getSortIcon(app.apparatus)}
                          </th>
                        ))}
                        <th className="gt-col-total sortable" onClick={() => handleSort('total')}>
                          <span className="app-full">Total</span>
                          <span className="app-abbr">AA</span>
                          {getSortIcon('total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const sorted = sortGymnasts(categoryGymnasts);
                        const paginated = paginateGymnasts(sorted, categoryKey);
                        const pageOffset = getCategoryPage(categoryKey) * ATHLETES_PER_PAGE;
                        return paginated.map((gymnast, idx) => {
                          const index = pageOffset + idx;
                          return (
                            <tr
                              key={gymnast.id}
                              className={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : (idx % 2 === 1 ? 'row-odd' : '')}
                            >
                              <td className="gt-col-rank">
                                <span className={`row-rank ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}>{index + 1}</span>
                              </td>
                              <td className="gt-col-name">
                                {gymnast.clubImage && (
                                  <img src={gymnast.clubImage} alt={gymnast.clubName} className="row-club-logo" />
                                )}
                                <span className="row-name">{gymnast.name}</span>
                              </td>
                              <td className="gt-col-club">{gymnast.clubName}</td>
                              {availableApparatus.map(app => (
                                <td
                                  key={app.code}
                                  className={`gt-col-app ${publicFilters.apparatus === app.name ? 'highlighted' : ''}`}
                                >
                                  {gymnast.scores[app.apparatus]?.toFixed(1) || '—'}
                                </td>
                              ))}
                              <td className="gt-col-total">{gymnast.total.toFixed(2)}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                  {/* Pagination */}
                  {categoryGymnasts.length > ATHLETES_PER_PAGE && (
                    <div className="table-pagination">
                      <button
                        className="pagination-btn"
                        disabled={getCategoryPage(categoryKey) === 0}
                        onClick={() => setCategoryPage(categoryKey, getCategoryPage(categoryKey) - 1)}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <div className="pagination-pages">
                        {Array.from({ length: getTotalPages(categoryGymnasts.length) }, (_, i) => (
                          <button
                            key={i}
                            className={`pagination-page ${getCategoryPage(categoryKey) === i ? 'active' : ''}`}
                            onClick={() => setCategoryPage(categoryKey, i)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        className="pagination-btn"
                        disabled={getCategoryPage(categoryKey) >= getTotalPages(categoryGymnasts.length) - 1}
                        onClick={() => setCategoryPage(categoryKey, getCategoryPage(categoryKey) + 1)}
                      >
                        <ChevronRight size={14} />
                      </button>
                      <span className="pagination-info">
                        {getCategoryPage(categoryKey) * ATHLETES_PER_PAGE + 1}-{Math.min((getCategoryPage(categoryKey) + 1) * ATHLETES_PER_PAGE, categoryGymnasts.length)} de {categoryGymnasts.length}
                      </span>
                    </div>
                  )}
                  {/* Mobile legend */}
                  <div className="table-legend">
                    {availableApparatus.map(app => (
                      <span key={app.code} className="legend-item">
                        <strong>{getApparatusAbbr(app.name)}</strong> = {app.name}
                      </span>
                    ))}
                    <span className="legend-item"><strong>AA</strong> = All-Around (Total)</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Clubs Summary - Accordion */}
      {(Array.isArray(currentClubs) ? currentClubs : []).length > 0 && (
        <div className="clubs-summary">
          <div className="clubs-summary-header" onClick={() => setClubsSummaryOpen(!clubsSummaryOpen)}>
            <div className="clubs-summary-title">
              <Building size={16} color="#18181b" />
              <h2>Resumen por Clubes</h2>
              <span className="clubs-count-badge">
                {(Array.isArray(currentClubs) ? currentClubs : []).length}
              </span>
            </div>
            {clubsSummaryOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>

          {clubsSummaryOpen && (
            <div className="clubs-summary-body">
              {/* Filtro de club */}
              <div className="clubs-filter">
                <select
                  value={clubsSummaryFilter}
                  onChange={(e) => setClubsSummaryFilter(e.target.value)}
                  className="clubs-filter-select"
                >
                  <option value="">Todos los clubes</option>
                  {(Array.isArray(currentClubs) ? currentClubs : []).map(club => (
                    <option key={club.id} value={club.name}>{club.name}</option>
                  ))}
                </select>
              </div>

              <div className="clubs-accordion">
                {(Array.isArray(currentClubs) ? currentClubs : [])
                  .filter(club => !clubsSummaryFilter || club.name === clubsSummaryFilter)
                  .map(club => {
                    const filteredGymnasts = club.gymnasts.filter(g => {
                      const levelMatch = !publicFilters.level || g.level === publicFilters.level;
                      const franjaMatch = !publicFilters.franja || g.franja === publicFilters.franja;
                      return levelMatch && franjaMatch;
                    });

                    // Ordenar por total descendente y tomar top 5
                    const top5 = [...filteredGymnasts]
                      .sort((a, b) => (b.total || 0) - (a.total || 0))
                      .slice(0, 5);

                    const avgAllRound = filteredGymnasts.length > 0
                      ? (filteredGymnasts.reduce((sum, g) => sum + g.allRound, 0) / filteredGymnasts.length).toFixed(2)
                      : '0.00';

                    const isClubExpanded = expandedClubs[club.id];

                    return (
                      <div key={club.id} className="club-accordion-item">
                        <div
                          className="club-accordion-header"
                          onClick={() => setExpandedClubs(prev => ({ ...prev, [club.id]: !prev[club.id] }))}
                        >
                          <div className="club-accordion-info">
                            {club.image_url ? (
                              <img src={club.image_url} alt={club.name} className="club-acc-logo" />
                            ) : (
                              <div className="club-acc-logo-placeholder">
                                <Building size={14} />
                              </div>
                            )}
                            <div className="club-acc-details">
                              <span className="club-acc-name">{club.name}</span>
                              <span className="club-acc-meta">
                                {filteredGymnasts.length} atleta{filteredGymnasts.length !== 1 ? 's' : ''} · Prom: {avgAllRound}
                              </span>
                            </div>
                          </div>
                          {isClubExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>

                        {isClubExpanded && (
                          <div className="club-accordion-body">
                            {top5.length === 0 ? (
                              <div className="club-no-athletes">Sin atletas con los filtros actuales</div>
                            ) : (
                              <table className="club-top-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Atleta</th>
                                    {availableApparatus.map(app => (
                                      <th key={app.code} className="club-top-app">
                                        <span className="app-full">{app.name}</span>
                                        <span className="app-abbr">{getApparatusAbbr(app.name)}</span>
                                      </th>
                                    ))}
                                    <th className="club-top-total">AA</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {top5.map((gymnast, idx) => (
                                    <tr key={gymnast.id} className={idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : ''}>
                                      <td className="club-top-rank">
                                        <span className={`row-rank ${idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : ''}`}>{idx + 1}</span>
                                      </td>
                                      <td className="club-top-name">{gymnast.name}</td>
                                      {availableApparatus.map(app => (
                                        <td key={app.code} className="club-top-app">
                                          {gymnast.scores[app.apparatus]?.toFixed(1) || '—'}
                                        </td>
                                      ))}
                                      <td className="club-top-total">{gymnast.total.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="results-disclaimer">
        <p>⚠️ Los puntajes aquí mostrados son preliminares e informativos. No constituyen resultados oficiales y pueden ser modificados por el panel de jueces en cualquier momento.</p>
      </div>
    </div>
  );
};

export default ResultsView;